import React, { useCallback, useState, useEffect } from 'react'
import { useRequest } from 'ahooks'
import styled from 'styled-components'
import Router from 'live/services/router'
import i18n from 'live/services/i18n'
import { FormEnabledConsumer } from 'live/context/form-enabled'
import AdminHeader from 'live/components/header/ui/admin/header'
import { PackageStatusTag } from './Tag'
import { PackageResource, PackageStatus } from '../http/api'
import { packageManagerHttpServicesClient, useOnRemoteChanges } from '../http/services'
import globalNotification from 'live/services/global-notification'
import { Active } from './styles'
import { getPackageDisplayStatus } from './utils'

const activeInfo = {
    ENABLED: ['far fa-check-circle', 'var(--green-700)', i18n('Active')],
    DISABLED: ['far fa-power-off', 'var(--gray-600)', i18n('Inactive')],
    INVALID: ['far fa-power-off', 'var(--gray-600)', i18n('Inactive')]
}

const PackagesList: React.VFC = () => {
    const [query, setQuery] = useState('')
    const { data, error, loading, run } = useRequest(() => packageManagerHttpServicesClient.getPackages())

    useOnRemoteChanges(run)

    const changeQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value)
    }, [])

    const clearQuery = useCallback(() => {
        setQuery('')
    }, [])

    const filteredData =
        data?.filter(
            pkg =>
                pkg.name?.toLowerCase()?.includes?.(query.toLowerCase()) ||
                pkg.title?.toLowerCase()?.includes?.(query.toLowerCase())
        ) ?? []
    const isEmpty = filteredData.length === 0

    useEffect(() => {
        if (error) {
            globalNotification.error(error.message)
        }
    }, [error])

    const body = (
        <>
            <SearchArea className="container-fluid filter-area">
                <div className="search-box">
                    <input
                        type="text"
                        value={query}
                        placeholder={i18n('Search packages')}
                        className="search no-box-shadow has-value"
                        onChange={changeQuery}
                    />
                </div>
            </SearchArea>
            <PackagesContainer>
                {isEmpty ? (
                    <BlankState didQuery={Boolean(query)} onClearQuery={clearQuery} loading={loading} />
                ) : (
                    <PackagesTable className="table table-hover">
                        <thead>
                            <tr>
                                <th className="col-md-1" />
                                <th className="col-md-6">{i18n('Name')}</th>
                                <th className="col-md-3">{i18n('Status')}</th>
                                <th className="col-md-2">{i18n('Validation')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map(pkg => (
                                <PackageItem key={pkg.uuid} package={pkg} />
                            ))}
                        </tbody>
                    </PackagesTable>
                )}
            </PackagesContainer>
        </>
    )

    return (
        <FormEnabledConsumer>
            {() => (
                <div className="admin-content">
                    <div className="admin-inner">
                        <AdminHeader>
                            <h2>{i18n('Packages')}</h2>
                        </AdminHeader>
                        {body}
                    </div>
                </div>
            )}
        </FormEnabledConsumer>
    )
}

interface PackageItemProps {
    package: PackageResource
}

const PackageItem: React.VFC<PackageItemProps> = ({ package: pkg }) => {
    const navigateToPackagePage = useCallback(() => {
        Router.setHash('packages/' + pkg.uuid)
    }, [pkg.uuid])

    const pkgName = pkg.title || pkg.name
    const status = getPackageDisplayStatus(pkg)
    const [activeClass, color, activeLabel] = activeInfo[status]

    return (
        <tr className="package-item" onClick={navigateToPackagePage}>
            <td className="col-md-1 package-icon">
                {pkg.iconBase64 && <img src={`data:image/png;base64,${pkg.iconBase64}`} alt="" />}
            </td>
            <td className="col-md-6 package-name">
                <span className="package-title">{pkgName}</span>
            </td>
            <td className="col-md-3 package-active">
                <Active color={color}>
                    <i className={activeClass} /> {i18n(activeLabel)}
                </Active>
            </td>
            <td className="col-md-2 package-tag">
                <PackageStatusTag package={pkg} />
            </td>
        </tr>
    )
}

interface BlankStateProps {
    didQuery?: boolean
    loading?: boolean

    onClearQuery(): void
}

const BlankState: React.VFC<BlankStateProps> = ({ didQuery, onClearQuery, loading }) => {
    return (
        <div className={`is-empty ${loading ? 'loading' : null}`}>
            <div>
                <p>
                    <strong>{i18n('No packages found')}</strong>
                </p>
                {didQuery && (
                    <p>
                        <button onClick={onClearQuery}>{i18n('Clear')}</button>
                    </p>
                )}
            </div>
        </div>
    )
}

const PackagesTable = styled.table`
    color: var(--gray-800);

    thead {
        font-weight: 700;
        font-size: 12px;
        line-height: 15px;
    }

    tbody {
        .package-item {
            min-height: 90px;
            cursor: pointer;

            .package-name {
                color: var(--gray-800);
                font-size: 18px;
            }
        }

        .package-icon {
            height: 80px;
        }

        .package-icon img {
            width: 48px;
            height: 48px;
            border-radius: 3px;
            object-fit: cover;
            background-color: grey;
        }

        .package-summary ul {
            margin: 0;
            padding-left: 1em;

            li {
                color: var(--gray-900);
                display: list-item;
                list-style: disc;
            }
        }
    }
`

const SearchArea = styled.div`
    width: 100%;
    position: sticky;
    top: 75px;
    padding: 35px 25px 0;
    z-index: 4;
    background-color: var(--gray-0);

    .search-box {
        width: 280px;
    }
`

const PackagesContainer = styled.div`
    width: 100%;
    min-width: 970px;
    padding: 75px 25px 80px;
`

export default PackagesList
